/**
 * Firebase PWA Integration
 *
 * This file contains utilities for implementing Firebase PWA features:
 * - Offline data persistence
 * - Authentication state management when offline
 * - Push notifications via Firebase Cloud Messaging
 */

import { getAuth } from "firebase/auth";
import { getMessaging, getToken, type Messaging } from "firebase/messaging";
import {
  disableNetwork as fsDisableNetwork,
  enableNetwork as fsEnableNetwork,
  doc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import app, { db as firestore } from "./firebaseConfig";

const auth = getAuth(app);
let messaging: Messaging | null = null;

// Check if messaging is supported in this browser
try {
  messaging = getMessaging(app);
} catch (error) {
  // Firebase Cloud Messaging not supported in this browser
}

/**
 * Disable network access for Firestore
 * All operations will use the cache while network is disabled
 */
export const disableNetwork = async (): Promise<void> => {
  await fsDisableNetwork(firestore);
};

/**
 * Enable network access for Firestore
 */
export const enableNetwork = async (): Promise<void> => {
  await fsEnableNetwork(firestore);
};

/**
 * Persist the device's FCM token on the signed-in user so the server can reach it
 * (functions/src/pushNotifications). Without this the token was fetched and discarded, so no
 * push could ever be delivered. arrayUnion keeps multiple devices; stale tokens are pruned
 * server-side on send failure.
 */
const saveFcmToken = async (token: string): Promise<void> => {
  const uid = auth.currentUser?.uid;
  if (!uid || !token) return;
  try {
    await updateDoc(doc(firestore, "users", uid), {
      fcmTokens: arrayUnion(token),
    });
  } catch (e) {
    console.error("Failed to save FCM token:", e);
  }
};

/**
 * Request permission for push notifications
 * @returns FCM token if permission granted, null otherwise
 */
export const requestNotificationPermission = async (): Promise<
  string | null
> => {
  if (!messaging) return null;

  try {
    // Check if notifications are supported
    if (!("Notification" in window)) {
      return null;
    }

    // Check if we're in a secure context
    if (!window.isSecureContext) {
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      try {
        // Let FCM auto-register /firebase-messaging-sw.js at its own push scope
        // (/firebase-cloud-messaging-push-scope) so background pushes reach OUR handler and
        // the Workbox sw.js at "/" is never touched. (Passing the Workbox registration here
        // routed background pushes to a SW that can't render them.)
        const token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY || "",
        });

        if (token) {
          await saveFcmToken(token);
          return token;
        }

        return null;
      } catch (tokenError) {
        console.error("Error getting FCM token:", tokenError);

        // Attempt fallback - get token without custom SW registration
        try {
          // Try without specifying the service worker registration
          const fallbackToken = await getToken(messaging, {
            vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY || "",
          });

          if (fallbackToken) {
            await saveFcmToken(fallbackToken);
            return fallbackToken;
          }
        } catch (fallbackError) {
          console.error("Fallback token retrieval also failed:", fallbackError);
        }

        return null;
      }
    }

    return null;
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return null;
  }
};

/**
 * Initialize all Firebase PWA features
 */
export const initializeFirebasePWA = async (): Promise<void> => {
  // Set up network detection
  window.addEventListener("online", async () => {
    // Re-enable network when back online
    await enableNetwork();
  });

  window.addEventListener("offline", async () => {
    // Disable network when offline to ensure using cached data
    await disableNetwork();
  });

  // Setup push notifications if supported and permission is already granted
  if (Notification.permission === "granted") {
    await requestNotificationPermission();
  }
};
